import {
  getEntityListDataFromLocalForage,
  _storeEntityDataListToLocalForage,
} from './mockDataStorageManager'
import type { IEntityDataItem } from '~/types/models'
import type { TFieldSetItem } from '~/types/fieldSet/models'
import { assertIsNotUndefined } from '~/types/models'
import { getPublicRuntimeConfig } from '~/cynqueModules/nuxtAccessors'

export type TListDataTypes = IEntityDataItem | TFieldSetItem

function conditionalConsoleDebug(message: string, obj?: any) {
  if (
    typeof window !== 'undefined' &&
    getPublicRuntimeConfig().doLimitMockDataLogOutput
  ) {
    return
  }
  console.debug(message, obj)
}

export function getListDataFunctionFactory<T extends TListDataTypes[]>({
  listData,
  storageKey,
  getStartingListData,
  sortFunction,
  forOfFunction,
  doSkipForEachFunction,
}: {
  listData: T
  storageKey: string
  getStartingListData: () => Promise<T>
  sortFunction?: (a: TListDataTypes, b: TListDataTypes) => number
  forOfFunction?: (arg0: TListDataTypes, arg1: boolean) => Promise<void>
  doSkipForEachFunction?: ({
    didUseEntityListDataFromStorage,
  }: {
    didUseEntityListDataFromStorage: boolean
  }) => boolean
}) {
  async function getListData(): Promise<T> {
    const fn = '[api/msw/modules/getListDataFunctionFactory.ts] getListData |'
    // ----------------------------------------------------------------------------
    // Set false below if making manual changes to mock data and wanting to see
    // effect of those changes on browser refresh without clearing persisted data.
    // listData for an entity will still be persisted if user makes changes
    // to data in the UI using an 'Edit' menu function or 'Create' (plus sign).

    const doStoreStartingListDataOnRead = true
    // ----------------------------------------------------------------------------
    //
    let didUseEntityListDataFromStorage = false
    if (!listData) {
      conditionalConsoleDebug(
        `${fn} ${storageKey} - listData is falsey to start. Trying to set listData`
      )
      const entityListDataFromStorage =
        await getEntityListDataFromLocalForage<T>(storageKey)
      if (entityListDataFromStorage) {
        conditionalConsoleDebug(
          `${fn} ${storageKey} - Using listData retrieved from localForage`
        )

        didUseEntityListDataFromStorage = true
      } else {
        conditionalConsoleDebug(
          `${fn} ${storageKey} - Gonna use list data from ${
            doStoreStartingListDataOnRead
              ? 'getStartingListDataAndStore()'
              : 'getStartingListData()'
          }`
        )
      }

      listData =
        entityListDataFromStorage ||
        (doStoreStartingListDataOnRead
          ? await getStartingListDataAndStore<T>({
              storageKey,
              getStartingListData,
              callingFn: fn,
            })
          : await getStartingListData())
    }

    assertIsNotUndefined<T>(listData)
    if (forOfFunction) {
      if (doSkipForEachFunction?.({ didUseEntityListDataFromStorage })) {
        conditionalConsoleDebug(
          `${fn} ${storageKey} - there is a forEach function,` +
            ` but doSkipForEachFunction() is returning true`
        )
      } else {
        conditionalConsoleDebug(
          `${fn} ${storageKey} - executing forOfFunction iterations.` +
            ` didUseEntityListDataFromStorage is ${didUseEntityListDataFromStorage}`
        )
        for (const item of listData) {
          await forOfFunction(item, didUseEntityListDataFromStorage)
        }
        if (doStoreStartingListDataOnRead) {
          await _storeEntityDataListToLocalForage({
            entityKeyBase: storageKey,
            listData,
            callingFn: fn,
          })
        }
      }
    }
    if (sortFunction) {
      // console.debug(
      //   `${storageKey} - Would be running sort function, but it is currently disabled.`
      // )
      conditionalConsoleDebug(`${fn} ${storageKey} - Running sort function`)
      listData.sort(sortFunction)
    }
    conditionalConsoleDebug(
      `${fn} ${storageKey} - returning listData`,
      listData
    )

    return listData
  }
  return getListData // This is the function created above
}

async function getStartingListDataAndStore<T extends TListDataTypes[]>({
  storageKey,
  getStartingListData,
  callingFn,
}: {
  storageKey: string
  getStartingListData: () => Promise<T>
  callingFn: string
}) {
  const listData = await getStartingListData()
  const entityKeyBase = storageKey.replace('esk_', '')
  await _storeEntityDataListToLocalForage({
    entityKeyBase,
    listData,
    callingFn,
  })
  return listData
}
